// Delivery with the restaurant's own riders (docs/plans/takeaway-delivery.md).
// A delivery order is an order with no table and a customer; its delivery row
// holds the address and rider. Dispatch is quick bill's checkout of the order
// (cash on delivery leaves the invoice unpaid), and the rider's cash is
// collected here later as ordinary payments.
import {
  collectRiderCash,
  formatMoney,
  markDelivered,
  markFailed,
  keepTyped,
  openDelivery,
  readAmountText,
  riderCash,
  updateDelivery,
  type RiderDelivery,
} from '@ims/core'
import type { CustomerRow, DeliveryDetail, TenantSettingsRow } from '@ims/data'
import { Badge, Button, Card, Field, Input, Modal, Switch, useToast } from '@ims/ui'
import { Bike } from '@ims/ui/icons'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useLive, useSettings } from '../hooks.ts'
import { money, todayIso } from '../lib/format.ts'
import { finePointer } from '../lib/pointer.ts'
import { deliveryRow, orderName, startOfToday } from '../lib/orders.ts'
import { insertOp, newCustomer, newProduct, updateOp } from '../lib/rows.ts'
import { useUser } from '../session.tsx'
import { useCan, useRepo, useWorkspace } from '../workspace.tsx'
import { useBuildContext, useWrite } from './order-hooks.ts'

const DELIVERY_SKU = 'DELIVERY'
const DEFAULT_FEE_PAISA = 10_000

/** The fee as typed ("100", "Rs 75.50"), or null when it is not an amount. */
function feeOf(text: string): number | null {
  return readAmountText(text, 2)?.value ?? null
}

const rupees = (paisa: number) => formatMoney(paisa, { lakhGrouping: false, alwaysDecimals: false })

/** The delivery product, when delivery is on. */
function useDeliveryProduct(settings: TenantSettingsRow | null | undefined) {
  const id = settings?.delivery_product_id ?? null
  return useLive((r) => (id ? r.get('products', id) : Promise.resolve(null)), [id], ['products'])
    .data
}

// -----------------------------------------------------------------------------
// Settings
// -----------------------------------------------------------------------------

/**
 * Turns delivery on by making (or finding again) the "Delivery charge"
 * product; its price is the default fee, changed on each order as needed.
 */
export function DeliveryCard({ settings }: { settings: TenantSettingsRow }) {
  const repo = useRepo()
  const { current } = useWorkspace()
  const canAdmin = useCan('admin')
  const { write, busy } = useWrite()
  const toast = useToast()
  const product = useDeliveryProduct(settings)
  const on = settings.delivery_product_id !== null
  const [feeText, setFeeText] = useState<string | null>(null)
  const shown = feeText ?? (product ? rupees(product.sell_price_paisa) : '')
  const fee = feeOf(shown)

  const switchOn = async () => {
    const ctx = { tenantId: current!.tenant_id }
    const found = (await repo.list('products', { where: { sku: DELIVERY_SKU }, limit: 1 })).rows[0]
    const made = found
      ? null
      : newProduct(ctx, {
          sku: DELIVERY_SKU,
          name: 'Delivery charge',
          sell_price_paisa: DEFAULT_FEE_PAISA,
          // Menu prices include everything; VAT follows the business's default rate.
          price_includes_tax: true,
          tax_rate_id: settings.default_tax_rate_id,
          track_stock: false,
          kitchen_station: 'none',
        })
    const productId = found?.id ?? made!.id
    const productOps = [
      ...(made ? [insertOp('products', made)] : []),
      ...(found && (!found.is_active || !found.for_sale || found.deleted_at)
        ? [updateOp('products', found, { is_active: true, for_sale: true, deleted_at: null })]
        : []),
    ]
    // Two txs, product first: the server applies tenant_settings before products, so in
    // one tx the setting's foreign key to the new product fails and both are rejected.
    if (productOps.length > 0 && !(await write(() => productOps))) return
    await write(() => [updateOp('tenant_settings', settings, { delivery_product_id: productId })])
  }

  const saveFee = async () => {
    if (!product || fee === null || fee === product.sell_price_paisa) return
    if (await write(() => [updateOp('products', product, { sell_price_paisa: fee })])) {
      setFeeText(null)
      toast.success('Delivery fee saved')
    }
  }

  return (
    <Card>
      <h2 className="font-semibold">Delivery</h2>
      <p className="mb-3 text-sm text-muted">
        Orders delivered by your own riders. Each one has a customer and an address, goes to the
        kitchen like any order, and is billed when it leaves. Cash on delivery is collected from the
        rider afterwards.
      </p>
      <Switch
        className="max-w-md"
        label="Take delivery orders"
        description="Adds a Delivery button to the Tables screen."
        checked={on}
        disabled={!canAdmin || busy}
        onChange={(e) =>
          void (e.target.checked
            ? switchOn()
            : write(() => [updateOp('tenant_settings', settings, { delivery_product_id: null })]))
        }
      />
      {on && product && (
        <form
          className="mt-4 flex max-w-md items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            void saveFee()
          }}
        >
          <Field
            className="flex-1"
            label="Delivery fee"
            hint="The usual fee, VAT included. It can be changed on each order."
            error={fee === null && shown !== '' ? 'Enter an amount' : undefined}
          >
            {(id) => (
              <Input
                id={id}
                inputMode="decimal"
                disabled={!canAdmin}
                value={shown}
                onChange={(e) => setFeeText(keepTyped(e.target.value, shown))}
                onBlur={() => setFeeText(readAmountText(shown, 2)?.tidy ?? shown)}
              />
            )}
          </Field>
          <Button
            type="submit"
            variant="secondary"
            loading={busy}
            disabled={!canAdmin || fee === null || fee === product.sell_price_paisa}
          >
            Save
          </Button>
        </form>
      )}
    </Card>
  )
}

// -----------------------------------------------------------------------------
// A new delivery
// -----------------------------------------------------------------------------

/** Phone first: a known customer fills in the name and address. */
export function NewDeliveryDialog({ onClose }: { onClose: () => void }) {
  const repo = useRepo()
  const settings = useSettings()
  const product = useDeliveryProduct(settings)
  const context = useBuildContext()
  const me = useUser().id
  const navigate = useNavigate()
  const { write, busy } = useWrite()
  const [phone, setPhone] = useState('')
  // What was typed; null means not touched, so a known customer's details show.
  const [nameText, setName] = useState<string | null>(null)
  const [addressText, setAddress] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [feeText, setFeeText] = useState<string | null>(null)
  const shownFee = feeText ?? (product ? rupees(product.sell_price_paisa) : '0')
  const fee = feeOf(shownFee)

  // The phone number is looked up as it is typed; a match fills in the name and address.
  const lookup = phone.replace(/\D/g, '').length >= 7 ? phone.trim() : null
  const known: CustomerRow | null =
    useLive(
      async (r) =>
        lookup
          ? ((await r.list('customers', { where: { phone: lookup }, limit: 1 })).rows[0] ?? null)
          : null,
      [lookup],
      ['customers'],
    ).data ?? null
  const name = nameText ?? known?.name ?? ''
  const address = addressText ?? known?.address ?? ''

  const valid = name.trim() !== '' && address.trim() !== '' && fee !== null
  const start = async () => {
    if (!valid || !settings) return
    const ctx = context()
    const last = await repo.orders.lastOrderNo(startOfToday())
    const customer =
      known ??
      newCustomer(ctx, { name: name.trim(), phone: phone.trim() || null, address: address.trim() })
    const taxId = product?.tax_rate_id ?? settings.default_tax_rate_id ?? null
    const tax = taxId ? await repo.get('tax_rates', taxId) : null
    const built = openDelivery(ctx, {
      orderNo: last + 1,
      customerId: customer.id,
      label: name,
      serverId: me,
      address,
      phone: phone || known?.phone || null,
      note,
      fee:
        product && fee
          ? {
              productId: product.id,
              description: product.name,
              qtyMilli: 1000,
              unitPricePaisa: fee,
              priceIncludesTax: product.price_includes_tax,
              taxRateId: tax?.id ?? null,
              taxRateBp: tax?.rate_bp ?? 0,
            }
          : null,
    })
    const ok = await write(() => [
      ...(known ? [] : [insertOp('customers', customer)]),
      ...built.ops,
    ])
    if (ok) {
      onClose()
      navigate(`/tables/${built.order.id}`)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      area="take"
      icon={<Bike className="size-5" />}
      title="New delivery"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={!valid} onClick={() => void start()}>
            Start order
          </Button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          void start()
        }}
      >
        <Field label="Phone" hint={known ? `${known.name}, a customer already` : undefined}>
          {(id) => (
            <Input
              id={id}
              type="tel"
              inputMode="tel"
              autoFocus={finePointer}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          )}
        </Field>
        <Field label="Name">
          {(id) => (
            <Input id={id} maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
          )}
        </Field>
        <Field label="Address">
          {(id) => (
            <Input
              id={id}
              maxLength={300}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          )}
        </Field>
        <Field label="Landmark or note" hint="Optional: “blue gate, 2nd floor”.">
          {(id) => (
            <Input id={id} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
          )}
        </Field>
        <Field
          label="Delivery fee"
          error={fee === null ? 'Enter an amount' : undefined}
          hint="0 for free delivery."
        >
          {(id) => (
            <Input
              id={id}
              inputMode="decimal"
              value={shownFee}
              onChange={(e) => setFeeText(keepTyped(e.target.value, shownFee))}
              onBlur={() => setFeeText(readAmountText(shownFee, 2)?.tidy ?? shownFee)}
            />
          )}
        </Field>
      </form>
    </Modal>
  )
}

// -----------------------------------------------------------------------------
// On the order screen
// -----------------------------------------------------------------------------

/** Where the order goes, and who takes it (changeable until it leaves). */
export function DeliveryPanel({ orderId, canServe }: { orderId: string; canServe: boolean }) {
  const delivery = useLive((r) => r.deliveries.forOrder(orderId), [orderId], ['deliveries']).data
  const riders = useRiderNames()
  const context = useBuildContext()
  const { write, busy } = useWrite()
  const [rider, setRider] = useState<string | null>(null)
  if (!delivery) return null
  const shown = rider ?? delivery.rider_name ?? ''
  const pending = delivery.status === 'pending'
  const saveRider = () => {
    if (!pending || shown.trim() === (delivery.rider_name ?? '')) return
    void write(
      () => updateDelivery(context(), deliveryRow(delivery), { rider_name: shown }).ops,
    ).then((ok) => ok && setRider(null))
  }
  return (
    <Card className="mb-3 p-3 text-sm">
      <p className="flex items-start gap-2">
        <Bike className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />
        <span>
          <span className="font-medium">{delivery.address}</span>
          {delivery.phone && <span className="text-muted"> · {delivery.phone}</span>}
          {delivery.note && <span className="block text-muted">{delivery.note}</span>}
        </span>
      </p>
      {pending && canServe && (
        <form
          className="mt-2 flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            saveRider()
          }}
        >
          <Field className="flex-1" label="Rider">
            {(id) => (
              <Input
                id={id}
                list="delivery-riders"
                maxLength={60}
                value={shown}
                onChange={(e) => setRider(e.target.value)}
                onBlur={saveRider}
              />
            )}
          </Field>
          <datalist id="delivery-riders">
            {riders.map((r) => (
              <option key={r} value={r} />
            ))}
          </datalist>
          <Button type="submit" variant="secondary" loading={busy}>
            Save
          </Button>
        </form>
      )}
    </Card>
  )
}

/** Riders named on recent deliveries, for suggestions. */
function useRiderNames(): string[] {
  const active = useLive((r) => r.deliveries.active(), [], ['deliveries']).data ?? []
  return [...new Set(active.map((d) => d.delivery.rider_name).filter((r): r is string => !!r))]
}

// -----------------------------------------------------------------------------
// On the Tables screen: what is out, cash with riders, failed
// -----------------------------------------------------------------------------

function riderEntries(details: readonly DeliveryDetail[]): RiderDelivery[] {
  return details
    .filter((d) => d.invoice)
    .map((d) => ({
      delivery: deliveryRow(d.delivery),
      invoiceId: d.invoice!.id,
      customerId: d.order.customer_id,
      duePaisa: d.duePaisa ?? 0,
    }))
}

/** All the cash riders hold right now, and how many deliveries it is from (the dashboard). */
export function ridersCashTotal(details: readonly DeliveryDetail[]): {
  duePaisa: number
  deliveries: number
} {
  const groups = riderCash(riderEntries(details))
  return {
    duePaisa: groups.reduce((sum, g) => sum + g.duePaisa, 0),
    deliveries: groups.reduce((sum, g) => sum + g.deliveries.length, 0),
  }
}

/** Deliveries still to deal with (repo `deliveries.active()`), live. */
export function useActiveDeliveries(): DeliveryDetail[] | undefined {
  return useLive(
    (r) => r.deliveries.active(),
    [],
    ['deliveries', 'orders', 'sales_invoices', 'payments', 'payment_allocations', 'credit_notes'],
  ).data
}

/** What is out, cash with riders, and failed deliveries (the Delivery tab of Tables). */
export function DeliveriesBoard({
  details,
  canServe,
}: {
  details: DeliveryDetail[] | undefined
  canServe: boolean
}) {
  const settings = useSettings()
  const context = useBuildContext()
  const { write, busy } = useWrite()
  const toast = useToast()
  const [failing, setFailing] = useState<DeliveryDetail | null>(null)
  const [reason, setReason] = useState('')
  const [collecting, setCollecting] = useState<string | null | undefined>(undefined)

  if (!details) return null
  const out = details.filter((d) => d.delivery.status === 'out')
  const failed = details.filter((d) => d.delivery.status === 'failed')
  const cash = riderCash(riderEntries(details))
  if (out.length === 0 && failed.length === 0 && cash.length === 0) return null
  const riderLabel = (rider: string | null) => rider ?? 'No rider named'

  return (
    <Card className="mt-6 p-0">
      <h2 className="border-b border-border px-4 py-3 text-sm font-semibold">Deliveries</h2>
      {out.length > 0 && (
        <ul className="divide-y divide-border" aria-label="Out for delivery">
          {out.map((d) => (
            <li
              key={d.delivery.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 text-sm"
            >
              <span className="min-w-0 flex-1">
                <span className="font-semibold">{orderName(d.order, null)}</span>
                <span className="text-muted">
                  {' '}
                  · {d.delivery.address} · out with {riderLabel(d.delivery.rider_name)}
                </span>
                {(d.duePaisa ?? 0) > 0 && (
                  <Badge tone="warning" className="ml-2">
                    COD {money(d.duePaisa!, settings)}
                  </Badge>
                )}
              </span>
              {canServe && (
                <span className="flex gap-1">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() =>
                      void write(() => markDelivered(context(), deliveryRow(d.delivery)).ops)
                    }
                  >
                    Delivered
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setFailing(d)}>
                    Failed
                  </Button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {cash.length > 0 && (
        <div className="border-t border-border">
          <h3 className="px-4 pt-3 text-sm font-semibold">Cash with riders</h3>
          <ul className="divide-y divide-border">
            {cash.map((c) => (
              <li
                key={c.rider ?? ''}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm"
              >
                <span>
                  <span className="font-medium">{riderLabel(c.rider)}</span>
                  <span className="text-muted">
                    {' '}
                    · {c.deliveries.length} {c.deliveries.length === 1 ? 'bill' : 'bills'}
                  </span>
                </span>
                <span className="flex items-center gap-3">
                  <span className="tabular font-semibold">{money(c.duePaisa, settings)}</span>
                  {canServe && (
                    <Button
                      size="sm"
                      loading={busy && collecting === c.rider}
                      aria-label={`Collect from ${riderLabel(c.rider)}`}
                      onClick={() => {
                        setCollecting(c.rider)
                        const built = collectRiderCash(context(), c.deliveries, todayIso())
                        void write(() => built.ops).then((ok) => {
                          setCollecting(undefined)
                          if (ok)
                            toast.success(
                              `Collected ${money(built.totalPaisa, settings)} from ${riderLabel(c.rider)}`,
                            )
                        })
                      }}
                    >
                      Collect
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {failed.length > 0 && (
        <div className="border-t border-border">
          <h3 className="px-4 pt-3 text-sm font-semibold">Failed: cancel the bill</h3>
          <ul className="divide-y divide-border">
            {failed.map((d) => (
              <li
                key={d.delivery.id}
                className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
              >
                <span>
                  <span className="font-semibold">{orderName(d.order, null)}</span>
                  <span className="text-muted"> · {d.delivery.failed_reason}</span>
                </span>
                {d.invoice && (
                  <Link
                    to={`/invoices/${d.invoice.id}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {d.invoice.number ?? 'Invoice'}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {failing && (
        <Modal
          open
          onClose={() => setFailing(null)}
          title={`${orderName(failing.order, null)} failed?`}
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setFailing(null)}>
                Back
              </Button>
              <Button
                variant="danger"
                loading={busy}
                disabled={reason.trim() === ''}
                onClick={() =>
                  void write(
                    () => markFailed(context(), deliveryRow(failing.delivery), reason).ops,
                  ).then((ok) => {
                    if (!ok) return
                    setFailing(null)
                    setReason('')
                  })
                }
              >
                Mark failed
              </Button>
            </>
          }
        >
          <p className="mb-3 text-sm text-muted">
            The bill stays until a manager cancels it from the invoice, which also puts back any
            stock.
          </p>
          <Field label="Reason">
            {(id) => (
              <Input
                id={id}
                autoFocus={finePointer}
                maxLength={300}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
        </Modal>
      )}
    </Card>
  )
}
